<template>
  <label :class="wrapperClasses">
    <input
      :id="id"
      :name="name"
      type="checkbox"
      :value="value"
      :checked="isChecked"
      :disabled="disabled"
      @change="onChange"
    />

    <span :class="controlClasses">
      <IconBase v-if="isChecked" :class="style.icon" :icon="checkIcon" />
    </span>

    <span v-if="text" :class="style.text">
      <slot>
        {{ text }}
      </slot>
    </span>

    <slot v-if="!text" />
  </label>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";
import style from "./CheckboxComponent.module.scss";
import checkIcon from "$images/icons/input/Name=Check, Size=16, Tone=Default.svg";
import IconBase from "@/elements/UI/icon/IconBase.vue";

export default defineComponent({
  name: "CheckboxComponent",
  components: {IconBase},
  emits: ["update:modelValue", "change"],
  props: {
    id: {
      type: String,
      default: ""
    },
    modelValue: {
      type: [Array, Boolean] as PropType<CheckboxValue[] | boolean>,
      default: false
    },
    value: {
      type: [String, Number, Boolean, Object] as PropType<CheckboxValue>,
      default: true
    },
    text: {
      type: String,
      default: ""
    },
    name: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    },
    size: {
      type: String as PropType<Size>,
      default: "L"
    }
  },

  data() {
    return {
      style,
      checkIcon
    };
  },

  computed: {
    isChecked(): boolean {
      if (Array.isArray(this.modelValue)) {
        return this.modelValue.includes(this.value);
      }

      return this.modelValue;
    },

    wrapperClasses(): string[] {
      return [
        style.checkbox,
        style[`size${this.size}`],
        this.disabled ? style.disabled : ""
      ];
    },

    controlClasses(): string[] {
      return [style.control];
    }
  },

  methods: {
    onChange(): void {
      if (this.disabled) {
        return;
      }

      if (Array.isArray(this.modelValue)) {
        const nextValue = [...this.modelValue];
        const index = nextValue.indexOf(this.value);

        if (index === -1) {
          nextValue.push(this.value);
        } else {
          nextValue.splice(index, 1);
        }

        this.$emit("update:modelValue", nextValue);
        this.$emit("change", nextValue);
        return;
      }

      const nextValue = !this.modelValue;

      this.$emit("update:modelValue", nextValue);
      this.$emit("change", nextValue);
    }
  }
});
</script>
